import { useState, useRef, useCallback } from 'react';
const SWIPE_THRESHOLD = 50; // pixels
const LONG_PRESS_DELAY = 500; // milliseconds
/**
 * Custom hook for gesture detection (tap, swipe, long-press)
 * Includes haptic feedback for iOS
 */
export function useLampshadeGestures(gestureHandlers = {}, toggleOnTap = true) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const swipeStartRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const touchStartTimeRef = useRef(0);
    // Haptic feedback support (iOS)
    const hapticFeedback = useCallback((type = 'light') => {
        if ('vibrate' in navigator) {
            const patterns = {
                light: 10,
                medium: 20,
                heavy: [30, 10, 30],
            };
            navigator.vibrate(patterns[type]);
        }
    }, []);
    const handleTouchStart = useCallback((e) => {
        if (e.touches.length !== 1)
            return;
        const touch = e.touches[0];
        touchStartTimeRef.current = Date.now();
        swipeStartRef.current = {
            startX: touch.clientX,
            startY: touch.clientY,
            endX: touch.clientX,
            endY: touch.clientY,
        };
        // Set up long-press timer
        longPressTimerRef.current = setTimeout(() => {
            hapticFeedback('heavy');
            gestureHandlers.onLongPress?.();
        }, LONG_PRESS_DELAY);
    }, [gestureHandlers, hapticFeedback]);
    const handleTouchEnd = useCallback((e) => {
        if (!swipeStartRef.current)
            return;
        // Clear long-press timer
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        const touch = e.changedTouches[0];
        swipeStartRef.current.endX = touch.clientX;
        swipeStartRef.current.endY = touch.clientY;
        const { startX, startY, endX, endY } = swipeStartRef.current;
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const elapsed = Date.now() - touchStartTimeRef.current;
        // Detect swipes (only if not a long-press)
        if (elapsed < LONG_PRESS_DELAY) {
            const isSwipe = Math.abs(deltaX) > SWIPE_THRESHOLD || Math.abs(deltaY) > SWIPE_THRESHOLD;
            if (isSwipe) {
                // Determine swipe direction
                const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
                if (isHorizontal) {
                    if (deltaX > SWIPE_THRESHOLD) {
                        hapticFeedback('light');
                        gestureHandlers.onSwipeRight?.();
                    }
                    else if (deltaX < -SWIPE_THRESHOLD) {
                        hapticFeedback('light');
                        gestureHandlers.onSwipeLeft?.();
                    }
                }
                else {
                    if (deltaY > SWIPE_THRESHOLD) {
                        hapticFeedback('medium');
                        gestureHandlers.onSwipeDown?.();
                    }
                    else if (deltaY < -SWIPE_THRESHOLD) {
                        hapticFeedback('medium');
                        gestureHandlers.onSwipeUp?.();
                    }
                }
            }
        }
        swipeStartRef.current = null;
    }, [gestureHandlers, hapticFeedback]);
    const handleClick = useCallback(() => {
        if (toggleOnTap) {
            hapticFeedback('light');
            setIsCollapsed((prev) => !prev);
            gestureHandlers.onTap?.();
        }
    }, [toggleOnTap, gestureHandlers, hapticFeedback]);
    return {
        isCollapsed,
        setIsCollapsed,
        handlers: {
            onTouchStart: handleTouchStart,
            onTouchEnd: handleTouchEnd,
            onClick: handleClick,
        },
        hapticFeedback,
    };
}
